import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  Table,
  Typography,
  DatePicker,
  Select,
  message,
  Space,
  Button,
  Switch,
  Spin,
  Tag,
} from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { parseLocalizedName } from "../../../utils/localization";
import { orderDocNo } from "../../../shared/lib/documentNumbers";
import { exportCSV } from "../../../utils/csvExport";
import { useProductsCatalog, useCustomersCatalog } from "../../../hooks/useCatalogData";
import useTablePagination from "../../../shared/ui/useTablePagination";
import "../../../shared/styles/ListPages.css";

const { Title, Text } = Typography;

const poStatusColors = {
  draft: "default",
  ordered: "processing",
  received: "success",
  cancelled: "error",
  partial: "processing",
  paid: "success",
};

const paymentStatusLabelKey = (status) => {
  const s = Number(status);
  if (s === 1) return "reportSellingPayStatus1";
  if (s === 2) return "reportSellingPayStatus2";
  if (s === 3) return "reportSellingPayStatus3";
  if (s === 4) return "reportSellingPayStatus4";
  return "reportSellingPayStatusUnknown";
};

/**
 * @param {{ mode: 'buying' | 'selling' }} props
 */
const TradingLinesReport = ({ mode }) => {
  const { t, i18n } = useTranslation();
  const isBuying = mode === "buying";
  const endpoint = isBuying ? "/reports/buying" : "/reports/selling";
  const titleKey = isBuying ? "reportBuyingTitle" : "reportSellingTitle";

  const { data: products, loading: productsLoading } = useProductsCatalog();
  const { data: customers, loading: customersLoading } = useCustomersCatalog();
  const { pagination } = useTablePagination();

  const [vendors, setVendors] = useState([]);
  const [dateRange, setDateRange] = useState(() => [dayjs().subtract(30, "day"), dayjs()]);
  const [productId, setProductId] = useState(undefined);
  const [vendorId, setVendorId] = useState(undefined);
  const [customerId, setCustomerId] = useState(undefined);
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState([]);
  const [summary, setSummary] = useState({ total_quantity: 0, total_amount: 0, line_count: 0 });
  const [mainCode, setMainCode] = useState("IQD");

  useEffect(() => {
    if (!isBuying) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get("/vendors");
        if (!cancelled) setVendors(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setVendors([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isBuying]);

  const productOptions = useMemo(
    () =>
      products.map((p) => ({
        value: p.id,
        label: `${p.sku || ""} — ${parseLocalizedName(p.name, i18n.language, `#${p.id}`)}`,
      })),
    [products, i18n.language]
  );

  const customerOptions = useMemo(
    () =>
      customers.map((c) => ({
        value: c.id,
        label: c.name || `#${c.id}`,
      })),
    [customers]
  );

  const vendorOptions = useMemo(
    () =>
      vendors.map((v) => ({
        value: v.id,
        label: v.name,
      })),
    [vendors]
  );

  const fetchReport = useCallback(async () => {
    if (!dateRange?.[0] || !dateRange?.[1]) return;
    setLoading(true);
    try {
      const params = {
        start_date: dateRange[0].format("YYYY-MM-DD"),
        end_date: dateRange[1].format("YYYY-MM-DD"),
      };
      if (productId != null) params.product_id = productId;
      if (isBuying && vendorId != null) params.vendor_id = vendorId;
      if (!isBuying && customerId != null) params.customer_id = customerId;
      if (isBuying) params.include_cancelled_po = includeCancelled ? 1 : 0;
      else params.include_cancelled_orders = includeCancelled ? 1 : 0;

      const res = await apiClient.get(endpoint, { params });
      const d = res.data;
      setMainCode(d.main_currency_code || "IQD");
      const rawLines = Array.isArray(d.lines) ? d.lines : [];
      setLines(
        rawLines.map((row, index) => ({
          ...row,
          _rowKey: isBuying
            ? `${row.purchase_order_id}-${row.product_id}-${index}`
            : `${row.order_id}-${row.product_id}-${index}`,
        }))
      );
      setSummary(
        d.summary && typeof d.summary === "object"
          ? d.summary
          : { total_quantity: 0, total_amount: 0, line_count: 0 }
      );
    } catch (e) {
      message.error(e?.response?.data?.message || t("reportTradingLoadFailed"));
      setLines([]);
    } finally {
      setLoading(false);
    }
  }, [
    dateRange,
    productId,
    vendorId,
    customerId,
    includeCancelled,
    endpoint,
    isBuying,
    t,
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const applyPreset = (days) => {
    setDateRange([dayjs().subtract(days, "day"), dayjs()]);
  };

  const buyingColumns = useMemo(
    () => [
      {
        title: "PO",
        key: "po",
        width: 88,
        render: (_, row) => (
          <Link to={`/purchase-orders/${row.purchase_order_id}/edit`}>#{row.purchase_order_id}</Link>
        ),
      },
      {
        title: t("date"),
        dataIndex: "po_date",
        key: "po_date",
        width: 150,
        render: (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "—"),
      },
      { title: t("purchaseOrdersColVendor"), dataIndex: "vendor_name", key: "vendor_name", ellipsis: true },
      {
        title: t("products"),
        key: "product",
        ellipsis: true,
        render: (_, row) => parseLocalizedName(row.product_name, i18n.language, row.product_sku || "—"),
      },
      { title: t("sku"), dataIndex: "product_sku", key: "sku", width: 110 },
      { title: t("purchaseOrdersLineQty"), dataIndex: "quantity", key: "qty", width: 72 },
      {
        title: t("purchaseOrdersLinePrice"),
        dataIndex: "single_price",
        key: "single_price",
        align: "right",
        render: (v) => formatCurrency(v, mainCode),
      },
      {
        title: t("purchaseOrdersLineSubtotal"),
        dataIndex: "line_total",
        key: "line_total",
        align: "right",
        render: (v) => formatCurrency(v, mainCode),
      },
      {
        title: t("purchaseOrdersColStatus"),
        dataIndex: "po_status",
        key: "po_status",
        render: (s) => (
          <Tag color={poStatusColors[s] || "default"}>{t(`poStatus_${s}`, { defaultValue: s })}</Tag>
        ),
      },
    ],
    [i18n.language, mainCode, t]
  );

  const sellingColumns = useMemo(
    () => [
      {
        title: t("orders"),
        key: "order",
        width: 150,
        render: (_, row) => (
          <Link to={`/orders/${row.order_id}/view`} style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700 }}>
            {orderDocNo(row)}
          </Link>
        ),
      },
      {
        title: t("date"),
        dataIndex: "order_date",
        key: "order_date",
        width: 150,
        render: (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "—"),
      },
      { title: t("customer"), dataIndex: "customer_name", key: "customer_name", ellipsis: true },
      {
        title: t("products"),
        key: "product",
        ellipsis: true,
        render: (_, row) => parseLocalizedName(row.product_name, i18n.language, row.product_sku || "—"),
      },
      { title: t("sku"), dataIndex: "product_sku", key: "sku", width: 110 },
      { title: t("purchaseOrdersLineQty"), dataIndex: "quantity", key: "qty", width: 72 },
      {
        title: t("purchaseOrdersLinePrice"),
        dataIndex: "single_price",
        key: "single_price",
        align: "right",
        render: (v) => formatCurrency(v, mainCode),
      },
      {
        title: t("purchaseOrdersLineSubtotal"),
        dataIndex: "line_total",
        key: "line_total",
        align: "right",
        render: (v) => formatCurrency(v, mainCode),
      },
      {
        title: t("payment"),
        dataIndex: "payment_status",
        key: "payment_status",
        render: (s) => t(paymentStatusLabelKey(s)),
      },
    ],
    [i18n.language, mainCode, t]
  );

  const columns = isBuying ? buyingColumns : sellingColumns;

  const handleExportCSV = () => {
    const csvCols = isBuying
      ? [
          { key: "purchase_order_id", title: "PO" },
          { key: "po_date", title: t("date") },
          { key: "vendor_name", title: t("purchaseOrdersColVendor") },
          { key: "product_sku", title: t("sku") },
          { key: "quantity", title: t("purchaseOrdersLineQty") },
          { key: "single_price", title: t("purchaseOrdersLinePrice") },
          { key: "line_total", title: t("purchaseOrdersLineSubtotal") },
          { key: "po_status", title: t("purchaseOrdersColStatus") },
        ]
      : [
          { key: "document_no", title: t("orders") },
          { key: "order_date", title: t("date") },
          { key: "customer_name", title: t("customer") },
          { key: "product_sku", title: t("sku") },
          { key: "quantity", title: t("purchaseOrdersLineQty") },
          { key: "single_price", title: t("purchaseOrdersLinePrice") },
          { key: "line_total", title: t("purchaseOrdersLineSubtotal") },
          { key: "payment_status", title: t("payment") },
        ];
    const filename = `${mode}-report-${dayjs().format("YYYY-MM-DD")}`;
    const rows = isBuying
      ? lines
      : lines.map((row) => ({ ...row, document_no: row.document_no || orderDocNo(row) }));
    exportCSV(csvCols, rows, filename);
  };

  return (
    <div className="list-page">
      <Title level={4} style={{ marginBottom: 12 }}>
        {t(titleKey)}
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        {t("reportTradingHint")}
      </Text>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size={[8, 8]} align="center">
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>{t("dateRangeStart", "Start")}</span>
            <DatePicker
              value={dateRange[0]}
              onChange={(d) => d && dateRange[1] && setDateRange([d, dateRange[1]])}
              allowClear={false}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>{t("dateRangeEnd", "End")}</span>
            <DatePicker
              value={dateRange[1]}
              onChange={(d) => d && dateRange[0] && setDateRange([dateRange[0], d])}
              allowClear={false}
            />
          </div>
          <Button size="small" onClick={() => applyPreset(7)}>
            {t("reportPreset7Days")}
          </Button>
          <Button size="small" onClick={() => applyPreset(30)}>
            {t("reportPreset30Days")}
          </Button>
          <Button size="small" onClick={() => setDateRange([dayjs().startOf("month"), dayjs()])}>
            {t("reportPresetMonthToDate")}
          </Button>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("products")}
            style={{ minWidth: 220 }}
            loading={productsLoading}
            value={productId}
            onChange={setProductId}
            options={productOptions}
          />
          {isBuying ? (
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t("purchaseOrdersColVendor")}
              style={{ minWidth: 200 }}
              value={vendorId}
              onChange={setVendorId}
              options={vendorOptions}
            />
          ) : (
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t("customer")}
              style={{ minWidth: 200 }}
              loading={customersLoading}
              value={customerId}
              onChange={setCustomerId}
              options={customerOptions}
            />
          )}
          <Space>
            <Switch checked={includeCancelled} onChange={setIncludeCancelled} size="small" />
            <Text type="secondary">
              {isBuying ? t("reportIncludeCancelledPo") : t("reportIncludeCancelledOrders")}
            </Text>
          </Space>
          <Button size="small" type="primary" onClick={fetchReport}>
            {t("currencyTotalsRefresh")}
          </Button>
          <Button
            size="small"
            icon={<FileExcelOutlined />}
            onClick={handleExportCSV}
            disabled={lines.length === 0}
          >
            CSV
          </Button>
        </Space>
      </Card>

      <div className="velan-stats-row" style={{ marginBottom: 16 }}>
        <div style={{ padding: "12px 16px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{t("reportSummaryLines")}</Text>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{summary.line_count ?? 0}</div>
        </div>
        <div style={{ padding: "12px 16px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{t("reportSummaryQty")}</Text>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{summary.total_quantity ?? 0}</div>
        </div>
        <div style={{ padding: "12px 16px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{t("reportSummaryAmount")}</Text>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {formatCurrency(toApiNumber(summary.total_amount, 0), mainCode)}
          </div>
        </div>
      </div>

      <Spin spinning={loading}>
        <Card size="small" styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="_rowKey"
            size="small"
            columns={columns}
            dataSource={lines}
            pagination={pagination}
            scroll={{ x: 1100 }}
          />
        </Card>
      </Spin>
    </div>
  );
};

export function BuyingReport() {
  return <TradingLinesReport mode="buying" />;
}

export function SellingReport() {
  return <TradingLinesReport mode="selling" />;
}

export default TradingLinesReport;
